import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { ApiError } from '../../../lib/api/errors.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { useUpdateGuest } from '../api/mutations.js';
import { useGuest, useGuestPii } from '../api/queries.js';
import {
  type GuestDetail,
  type GuestFormInput,
  type GuestPii,
  guestFormInput,
} from '../api/schemas.js';
import { GuestFields } from '../forms/guest-fields.js';

const HTTP_CONFLICT = 409;

function defaultsFrom(detail: GuestDetail, pii: GuestPii | undefined): GuestFormInput {
  const address = detail.addresses?.[0];

  return {
    firstName: pii?.firstName ?? '',
    lastName: pii?.lastName ?? '',
    email: pii?.email ?? '',
    phone: pii?.phone ?? '',
    status: detail.status,
    accountStatus: detail.account?.hasAccount === true ? detail.account.status : undefined,
    address: {
      type: address?.type ?? 'HOME',
      line1: address?.line1 ?? '',
      line2: address?.line2 ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      postalCode: address?.postalCode ?? '',
      countryCode: address?.countryCode ?? '',
    },
  };
}

export function GuestFormPage(): React.JSX.Element {
  const guestId = useParams().guestId ?? '';

  return (
    <PermissionGuard permission={PERMISSIONS.GUEST_WRITE} fallback={<NotPermitted />}>
      <EditGuest guestId={guestId} />
    </PermissionGuard>
  );
}

function EditGuest({ guestId }: { readonly guestId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateGuest();
  const query = useGuest(guestId);
  const piiQuery = useGuestPii(guestId);

  if (query.isPending || piiQuery.isPending) {
    return <LoadingState label="Loading guest" />;
  }

  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  if (piiQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(piiQuery.error) ?? ''}
        onRetry={() => {
          void piiQuery.refetch();
        }}
      />
    );
  }

  return (
    <EditGuestForm
      detail={query.data}
      pii={piiQuery.data}
      onSaved={() => {
        void navigate(`/guests/${guestId}`);
      }}
      save={async (values) => {
        try {
          await update.mutateAsync({ guestId, version: query.data.version, values });
        } catch (error) {
          if (error instanceof ApiError && error.status === HTTP_CONFLICT) {
            await query.refetch();
          }
          throw error;
        }
      }}
    />
  );
}

function EditGuestForm({
  detail,
  pii,
  save,
  onSaved,
}: {
  readonly detail: GuestDetail;
  readonly pii: GuestPii | undefined;
  readonly save: (values: GuestFormInput) => Promise<void>;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const form = useAppForm<GuestFormInput>({
    schema: guestFormInput,
    defaultValues: defaultsFrom(detail, pii),
    successMessage: 'Guest updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={pii?.firstName ?? 'Guest'}
      description="Update the guest's status and contact details. The contact fields are encrypted at rest."
      backTo="/guests"
      backLabel="Guests"
      submitLabel="Save guest"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <GuestFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        hasAccount={detail.account?.hasAccount === true}
      />
    </FormPage>
  );
}
